import { useCallback, useState } from "react";

/**
 * Separates the conversation the URL names from the one the workspace mounted.
 *
 * The workspace is keyed by its conversation so privileged state cannot leak
 * between two threads (`workspaceContextKey`). But a thread created by the turn
 * the advocate just sent is the *same* conversation: if its new id entered the
 * mount key, React would remount the workspace mid-send and throw away the run
 * id it was about to stream, leaving a metered run with no visible output.
 *
 * So creation moves the address bar only (D142), and the mount identity changes
 * on navigation — opening a matter, starting new work, or the back button.
 */
export interface ThreadRouteState {
  /** What the address bar shows, including a just-created thread. */
  readonly threadId: string | null;
  /** Mount identity for the workspace; stable across thread creation. */
  readonly openedThreadId: string | null;
  /** Changes on explicit navigation, even when the target is another new thread. */
  readonly navigationRevision: number;
}

/** Navigation: a different conversation, so the workspace remounts for it. */
export const openedThreadRoute = (
  threadId: string | null,
  navigationRevision = 0,
): ThreadRouteState => ({
  threadId,
  openedThreadId: threadId,
  navigationRevision,
});

/** The open conversation was persisted: the URL moves, the mount does not. */
export const createdThreadRoute = (
  state: ThreadRouteState,
  threadId: string,
): ThreadRouteState => ({ ...state, threadId });

export interface ThreadRoute extends ThreadRouteState {
  readonly openThread: (threadId: string | null) => void;
  readonly threadCreated: (threadId: string) => void;
}

export const useThreadRoute = (initialThreadId: string | null): ThreadRoute => {
  const [state, setState] = useState<ThreadRouteState>(() =>
    openedThreadRoute(initialThreadId),
  );

  const openThread = useCallback((next: string | null): void => {
    setState((current) => openedThreadRoute(next, current.navigationRevision + 1));
  }, []);

  const threadCreated = useCallback((next: string): void => {
    setState((current) => createdThreadRoute(current, next));
  }, []);

  return { ...state, openThread, threadCreated };
};
